import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { trackWeatherForecastCardToggled } from '../helpers/analytics/weatherForecastAnalytics';
import type { WeatherForecastCardName } from '../helpers/analytics/weatherForecastAnalytics';
import {
  getStoredWeatherForecastCardExpanded,
  loadWeatherForecastCardExpandedState,
  mergeHydratedWeatherForecastCardExpandedState,
  persistWeatherForecastCardExpanded,
  subscribeWeatherForecastCardExpanded,
} from '../helpers/storage/weatherForecastCardExpandedState';

export const useWeatherForecastCardExpanded = (
  card: WeatherForecastCardName | undefined,
  defaultExpanded: boolean,
) => {
  const sharedExpanded = useSyncExternalStore(
    subscribeWeatherForecastCardExpanded,
    () =>
      card
        ? getStoredWeatherForecastCardExpanded(card, defaultExpanded)
        : defaultExpanded,
    () => defaultExpanded,
  );

  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const isExpanded = card ? sharedExpanded : localExpanded;

  useEffect(() => {
    if (!card) {
      return;
    }

    let isMounted = true;

    loadWeatherForecastCardExpandedState().then((state) => {
      if (!isMounted) {
        return;
      }
      mergeHydratedWeatherForecastCardExpandedState(state);
    });

    return () => {
      isMounted = false;
    };
  }, [card]);

  const toggleExpanded = useCallback(() => {
    if (!card) {
      setLocalExpanded((current) => !current);
      return;
    }

    const nextExpanded = !getStoredWeatherForecastCardExpanded(
      card,
      defaultExpanded,
    );
    trackWeatherForecastCardToggled(card, nextExpanded);
    void persistWeatherForecastCardExpanded(card, nextExpanded);
  }, [card, defaultExpanded]);

  return { isExpanded, toggleExpanded };
};
